import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatAUD } from "@/lib/api";
import { Plus, ArrowUpRight, Bell, Check, TrendingUp, Clock, Target, MapPin, CalendarRange, DollarSign } from "lucide-react";

const fromCents = (c) => formatAUD(Number(c || 0) / 100);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });
  const [sales, setSales] = useState(null);
  const [window_, setWindow] = useState("all");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const loadAll = () => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/notifications?limit=15").then((r) => setNotifications(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    api.get(`/stats/sales-dashboard?window=${window_}`).then((r) => setSales(r.data)).catch(() => {});
  }, [window_]);

  const markRead = async (nid) => {
    await api.post(`/notifications/${nid}/read`);
    loadAll();
  };
  const markAllRead = async () => {
    await api.post(`/notifications/read-all`);
    loadAll();
  };

  const createProject = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", {
        title: "New Project", status: "draft", room_type: "Living Room",
        wall_width_mm: 3000, wall_height_mm: 2400, wall_depth_mm: 400,
        schematic: { items: [] }, line_items: [],
        labour_rate: 0.15, margin_rate: 0.20, tax_rate: 0.10, discount: 0,
      });
      navigate(`/projects/${data.id}`);
    } finally { setCreating(false); }
  };

  if (!stats) return <div className="p-12 text-muted-foreground">Loading…</div>;

  const cards = [
    { label: "New Leads", value: stats.new_leads || 0, accent: "text-[color:hsl(var(--accent))]", testid: "stat-new-leads" },
    { label: "Active Leads", value: stats.active_leads || 0, accent: "", testid: "stat-active-leads" },
    { label: "Draft Proposals", value: stats.draft, accent: "", testid: "stat-draft" },
    { label: "Sent Proposals", value: stats.sent, accent: "", testid: "stat-sent" },
    { label: "Accepted", value: stats.accepted, accent: "", testid: "stat-accepted" },
    { label: "Open Tasks", value: stats.open_tasks || 0, accent: "", testid: "stat-tasks" },
  ];

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-7xl" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
        <div>
          <div className="overline">Studio Overview</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>
            Welcome back.
          </h1>
          <p className="text-muted-foreground mt-3 max-w-xl">
            What needs your attention today — new leads, projects in design, and proposals awaiting client approval.
          </p>
        </div>
        <button data-testid="btn-new-project" onClick={createProject} disabled={creating} className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New Project
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-border mb-8 border border-border">
        {cards.map((c) => (
          <div key={c.label} className="bg-card p-7" data-testid={c.testid || `stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="overline text-[10px]" style={{ color: "#8A8A8A" }}>{c.label}</div>
            <div className={`font-serif font-light mt-3 ${c.accent}`} style={{ fontSize: "44px", lineHeight: 1 }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {/* Sales */}
      <div className="grid grid-cols-2 gap-px bg-border mb-16 border border-border">
        <div className="bg-card p-7" data-testid="stat-pipeline">
          <div className="overline text-[10px]">Pipeline Value</div>
          <div className="font-serif text-4xl font-light mt-3">${(stats.pipeline_value || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-muted-foreground mt-1">Draft + Sent proposals</div>
        </div>
        <div className="bg-card p-7" data-testid="stat-accepted-value">
          <div className="overline text-[10px]">Accepted Value</div>
          <div className="font-serif text-4xl font-light mt-3">${(stats.accepted_value || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-muted-foreground mt-1">Projects accepted by clients</div>
        </div>
      </div>

      {/* Needs Attention (notifications) */}
      <NeedsAttention notifications={notifications} onOpen={(n) => {
          if (n.project_id) {
            if (n.design_version_id) markRead(n.id).then(() => {});
            navigate(`/projects/${n.project_id}#dv-${n.design_version_id || ""}`);
          }
        }}
        onMarkRead={markRead}
        onMarkAll={markAllRead}/>

      {/* Sales dashboard */}
      <SalesDashboard sales={sales} window_={window_} setWindow={setWindow}/>

      {/* Recent projects */}
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="overline">01 · Recently Updated</div>
          <h2 className="font-serif font-light mt-2" style={{ fontSize: "32px" }}>Active Projects</h2>
        </div>
        <button data-testid="btn-view-all-projects" onClick={() => navigate("/projects")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          View all <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {stats.recent_projects?.length === 0 ? (
        <div className="luxe-card p-12 text-center">
          <div className="font-serif text-2xl mb-2">No projects yet</div>
          <p className="text-muted-foreground text-sm mb-6">Create your first media wall design and begin the proposal workflow.</p>
          <button data-testid="btn-empty-create" onClick={createProject} className="btn-accent mx-auto">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Create your first project
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-px bg-border border border-border">
          {stats.recent_projects.map((p) => (
            <button
              key={p.id}
              data-testid={`recent-project-${p.id}`}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="bg-card text-left p-8 hover:bg-[color:hsl(var(--muted))] transition-colors">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="overline text-[10px] mb-1" style={{ color: "#8A8A8A" }}>{p.room_type || "Custom"}</div>
                  <div className="font-serif text-2xl leading-tight">{p.title}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-medium px-2 py-1 ${statusClasses(p.status)}`}>
                  {p.status}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">{p.client_name || "No client linked"}</div>
              <div className="mt-6 flex items-end justify-between">
                <div>
                  <div className="overline text-[10px]" style={{ color: "#8A8A8A" }}>Total</div>
                  <div className="font-serif text-2xl mt-1">{formatAUD(p.totals?.total)}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Updated {new Date(p.updated_at).toLocaleDateString("en-AU")}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function statusClasses(s) {
  switch (s) {
    case "accepted": return "bg-[color:hsl(var(--accent))] text-white";
    case "sent": return "bg-foreground text-background";
    case "declined": return "bg-[color:hsl(var(--destructive))] text-white";
    default: return "bg-muted text-foreground";
  }
}

function NeedsAttention({ notifications, onOpen, onMarkRead, onMarkAll }) {
  const items = notifications?.items || [];
  const unread = notifications?.unread || 0;
  return (
    <div className="mb-16" data-testid="needs-attention">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="overline">Needs Attention</div>
          <h2 className="font-serif font-light mt-1" style={{ fontSize: "28px" }}>
            {unread > 0 ? `${unread} new item${unread === 1 ? "" : "s"}` : "You're all caught up"}
          </h2>
        </div>
        {unread > 0 && (
          <button data-testid="btn-mark-all-read" onClick={onMarkAll} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={1.5}/> Mark all read</button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="luxe-card p-6 text-sm text-muted-foreground">No notifications yet. Client feedback on released concepts will appear here.</div>
      ) : (
        <div className="luxe-card divide-y divide-border">
          {items.slice(0, 8).map((n) => (
            <div key={n.id} className={`p-4 flex items-start gap-4 ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
              <Bell className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: n.read ? "#8A8A8A" : "#A68A64" }}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{n.title}</div>
                {n.description && <div className="text-xs text-muted-foreground mt-1">{n.description}</div>}
                <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("en-AU")}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {n.project_id && (
                  <button data-testid={`btn-open-notif-${n.id}`} onClick={() => onOpen(n)} className="btn-outline text-xs">Open</button>
                )}
                {!n.read && (
                  <button data-testid={`btn-read-notif-${n.id}`} onClick={() => onMarkRead(n.id)} className="text-xs text-muted-foreground hover:text-foreground px-2">
                    <Check className="w-3 h-3" strokeWidth={1.5}/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Sales Dashboard ──────────────────────────────────────────────────
function SalesDashboard({ sales, window_, setWindow }) {
  if (!sales) return null;
  const wp = sales.weighted_pipeline;
  const funnel = sales.funnel.stages;
  const active = sales.active_value;
  const forecast = sales.forecast;
  const aging = sales.aging;
  const props = sales.proposal_metrics;
  const suburbs = sales.top_suburbs;
  const totalPipeMax = Math.max(1, ...wp.stages.map((s) => s.raw_cents));
  const funnelMax = Math.max(1, ...funnel.map((s) => s.count));

  return (
    <div className="mb-16" data-testid="sales-dashboard">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline">Sales & Reporting</div>
          <h2 className="font-serif font-light mt-1" style={{ fontSize: "32px" }}>Pipeline health</h2>
        </div>
        <div className="flex gap-1" data-testid="window-selector">
          {[["30d","30d"],["90d","90d"],["ytd","YTD"],["all","All time"]].map(([k,l]) => (
            <button key={k} data-testid={`window-${k}`} onClick={() => setWindow(k)}
              className={`px-3 py-1.5 text-xs border ${window_===k ? "bg-foreground text-background border-foreground" : "border-input hover:border-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <MetricCard icon={TrendingUp} label="Weighted pipeline" value={fromCents(wp.weighted_cents)} sub={`${fromCents(wp.raw_cents)} raw`} testid="metric-weighted"/>
        <MetricCard icon={DollarSign} label="Active project value" value={fromCents(active.active_value_cents)} sub={`${active.active_project_count} projects`} testid="metric-active"/>
        <MetricCard icon={Target} label="Accepted proposals" value={fromCents(props.accepted.total_cents)} sub={`avg ${fromCents(props.avg_accepted_cents)}`} testid="metric-accepted"/>
        <MetricCard icon={CalendarRange} label="Forecast 90d" value={fromCents(forecast.d30_cents + forecast.d60_cents + forecast.d90_cents)} sub={`+ ${fromCents(forecast.unscheduled_cents)} unscheduled`} testid="metric-forecast"/>
      </div>

      {/* Weighted pipeline bars */}
      <section className="luxe-card p-5 mb-6" data-testid="weighted-pipeline">
        <div className="overline mb-4">Weighted pipeline by stage</div>
        {wp.stages.every((s) => s.count === 0) ? (
          <EmptyBlock label="No open leads in pipeline"/>
        ) : (
          <div className="space-y-2">
            {wp.stages.map((s) => (
              <div key={s.stage} className="grid grid-cols-[110px,1fr,110px,80px] items-center gap-3 text-xs">
                <div className="capitalize text-muted-foreground">{s.stage.replaceAll("_", " ")}</div>
                <div className="h-4 bg-muted relative">
                  <div className="h-full" style={{ width: `${(s.raw_cents / totalPipeMax) * 100}%`, background: "#EDE9DF" }}/>
                  <div className="h-full absolute top-0 left-0" style={{ width: `${(s.weighted_cents / totalPipeMax) * 100}%`, background: "#A68A64" }}/>
                </div>
                <div className="text-right font-mono">{fromCents(s.weighted_cents)}</div>
                <div className="text-right text-muted-foreground">{s.count} leads</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Funnel + Aging + Suburbs */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <section className="luxe-card p-5" data-testid="conversion-funnel">
          <div className="overline mb-4">Conversion funnel</div>
          <div className="space-y-2">
            {funnel.map((s, i) => (
              <div key={s.key}>
                <div className="flex items-center justify-between text-xs">
                  <div>{s.label}</div>
                  <div className="font-mono">
                    {s.count}
                    {i > 0 && <span className="text-muted-foreground ml-2">({s.pct_of_prev}%)</span>}
                  </div>
                </div>
                <div className="h-2 bg-muted mt-1">
                  <div className="h-full" style={{ width: `${(s.count / funnelMax) * 100}%`, background: "#1C1C1C" }}/>
                </div>
              </div>
            ))}
            <div className="text-[10px] text-muted-foreground pt-2">Overall conversion: {sales.funnel.conversion_pct}%</div>
          </div>
        </section>

        <section className="luxe-card p-5" data-testid="pipeline-aging">
          <div className="overline mb-4 flex items-center gap-2"><Clock className="w-3 h-3" strokeWidth={1.5}/> Pipeline aging</div>
          {aging.stages.every((s) => s.count === 0) ? (
            <EmptyBlock label="No open leads"/>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left font-normal pb-1">Stage</th>
                  <th className="text-right font-normal pb-1">Count</th>
                  <th className="text-right font-normal pb-1">Avg days</th>
                  <th className="text-right font-normal pb-1">Stale</th>
                </tr>
              </thead>
              <tbody>
                {aging.stages.map((s) => (
                  <tr key={s.stage} className="border-t border-border">
                    <td className="py-1 capitalize">{s.stage.replaceAll("_", " ")}</td>
                    <td className="text-right">{s.count}</td>
                    <td className="text-right">{s.avg_days}</td>
                    <td className="text-right">{s.stale_count > 0 ? <span style={{ color: "#B85C38" }}>{s.stale_count}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-[10px] text-muted-foreground mt-3">Stale ≥ {aging.threshold_days} days in current stage.</div>
        </section>

        <section className="luxe-card p-5" data-testid="top-suburbs">
          <div className="overline mb-4 flex items-center gap-2"><MapPin className="w-3 h-3" strokeWidth={1.5}/> Top suburbs</div>
          {suburbs.by_lead_count.length === 0 ? (
            <EmptyBlock label="No suburb data yet"/>
          ) : (
            <ul className="text-xs space-y-1">
              {suburbs.by_lead_count.slice(0, 6).map((s) => (
                <li key={s.suburb} className="flex justify-between border-b border-border pb-1">
                  <span>{s.suburb || "Unknown"}</span>
                  <span className="text-muted-foreground">{s.count} leads · {s.conversion_pct}%</span>
                </li>
              ))}
            </ul>
          )}
          {suburbs.by_accepted_value.length > 0 && (
            <div className="mt-4 text-[10px] overline">By accepted value</div>
          )}
          <ul className="text-xs mt-2 space-y-1">
            {suburbs.by_accepted_value.slice(0, 5).map((s) => (
              <li key={s.suburb} className="flex justify-between">
                <span>{s.suburb || "Unknown"}</span>
                <span className="font-mono">{fromCents(s.value_cents)}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Forecast + Oldest opportunities */}
      <div className="grid lg:grid-cols-[1fr,1.4fr] gap-4">
        <section className="luxe-card p-5" data-testid="forecast-panel">
          <div className="overline mb-4 flex items-center gap-2"><CalendarRange className="w-3 h-3" strokeWidth={1.5}/> Weighted forecast</div>
          <div className="space-y-3">
            <ForecastRow label="Next 30 days" value_cents={forecast.d30_cents}/>
            <ForecastRow label="30-60 days" value_cents={forecast.d60_cents}/>
            <ForecastRow label="60-90 days" value_cents={forecast.d90_cents}/>
            <ForecastRow label="Unscheduled" value_cents={forecast.unscheduled_cents} muted/>
          </div>
          <div className="text-[10px] text-muted-foreground mt-4">{forecast.methodology}</div>
        </section>

        <section className="luxe-card p-5" data-testid="oldest-opportunities">
          <div className="overline mb-4">Longest-open opportunities</div>
          {aging.oldest.length === 0 ? (
            <EmptyBlock label="Pipeline is fresh"/>
          ) : (
            <ul className="divide-y divide-border">
              {aging.oldest.slice(0, 6).map((o) => (
                <li key={o.id} className="py-2 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-medium">{o.name} <span className="text-muted-foreground">· {o.suburb || "—"}</span></div>
                    <div className="text-muted-foreground capitalize">{(o.stage || "").replaceAll("_", " ")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{fromCents(o.value_cents)}</div>
                    <div style={{ color: o.age_days >= aging.threshold_days ? "#B85C38" : "inherit" }}>{o.age_days}d</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="luxe-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[color:hsl(var(--accent))]" strokeWidth={1.5}/>
        <div className="overline text-[10px]">{label}</div>
      </div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
function ForecastRow({ label, value_cents, muted }) {
  return (
    <div className={`flex items-center justify-between ${muted ? "text-muted-foreground" : ""}`}>
      <div className="text-xs">{label}</div>
      <div className="font-mono text-sm">{fromCents(value_cents)}</div>
    </div>
  );
}
function EmptyBlock({ label }) {
  return <div className="text-xs text-muted-foreground py-3">{label}</div>;
}
